
import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { lastValueFrom } from 'rxjs';
import { Advertiser, CampaignsAdvertiserAdminApiClient, ProfileFieldConfiguration, ProfileFieldSettings, DateOfBirthFieldSettings, DateOfBirthFormat } from 'src/app/api/api-campaigns.service.generated';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { LoadingAwareComponent } from 'src/app/common/utils/loading-aware.component';
import { PERMISSION } from 'src/app/const/permission';
import { ButtonComponent } from 'wecheer-common';

@Component({
  selector: 'app-advertiser-consumer-profile-config',
  templateUrl: './advertiser-consumer-profile-config.component.html',
  styleUrls: ['./advertiser-consumer-profile-config.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatTableModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    ButtonComponent,
    HasAnyPermissionDirective
]
})
export class AdvertiserConsumerProfileConfigComponent extends LoadingAwareComponent {
  _advertiser: Advertiser;
  get advertiser() {
    return this._advertiser;
  }
  @Input() set advertiser(value: Advertiser) {
    this._advertiser = value;
    this.handleConfiguration();
  }

  profileConfigForm: FormGroup;
  isSaving = false;
  readonly permissions = PERMISSION;

  dateFormats = [
    { value: 'FullDate', label: 'Full Date (Day/Month/Year)' },
    { value: 'MonthAndYear', label: 'Month and Year' },
    { value: 'DayAndMonth', label: 'Day and Month' },
    { value: 'YearOnly', label: 'Year Only' }
  ];

  profileFields = [
    {
      displayName: 'First Name',
      visibleControlName: 'firstNameVisible',
      requiredControlName: 'firstNameRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: false
    },
    {
      displayName: 'Last Name',
      visibleControlName: 'lastNameVisible',
      requiredControlName: 'lastNameRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: false
    },
    {
      displayName: 'Email',
      visibleControlName: 'emailVisible',
      requiredControlName: 'emailRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: false
    },
    {
      displayName: 'Gender',
      visibleControlName: 'genderVisible',
      requiredControlName: 'genderRequired',
      description: 'Options presented: Male, Female, Don\'t want to disclose',
      hasFormat: false,
      formatControlName: null,
      isSubField: false
    },
    {
      displayName: 'Date of Birth',
      visibleControlName: 'dateOfBirthVisible',
      requiredControlName: 'dateOfBirthRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: false,
      showCurrentFormat: true
    },
    {
      displayName: 'Day',
      visibleControlName: 'dayVisible',
      requiredControlName: 'dayRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: true
    },
    {
      displayName: 'Month',
      visibleControlName: 'monthVisible',
      requiredControlName: 'monthRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: true
    },
    {
      displayName: 'Year',
      visibleControlName: 'yearVisible',
      requiredControlName: 'yearRequired',
      description: null,
      hasFormat: false,
      formatControlName: null,
      isSubField: true
    }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private client: CampaignsAdvertiserAdminApiClient,
    private snackBar: MatSnackBar
  ) {
    super();
    this.initializeFormControls();
  }

  private getDefaultConfiguration() {
    return {
      firstNameVisible: true,
      firstNameRequired: true,
      lastNameVisible: true,
      lastNameRequired: true,
      emailVisible: true,
      emailRequired: true,
      genderVisible: true,
      genderRequired: false,
      dateOfBirthVisible: true,
      dateOfBirthRequired: true,
      dateOfBirthFormat: 'FullDate',
      dayVisible: true,
      monthVisible: true,
      yearVisible: true,
      dayRequired: true,
      monthRequired: true,
      yearRequired: true
    };
  }

  private initializeFormControls() {
    const defaultConfig = this.getDefaultConfiguration();

    this.profileConfigForm = this.formBuilder.group({
      // First Name
      firstNameVisible: [defaultConfig.firstNameVisible],
      firstNameRequired: [defaultConfig.firstNameRequired],

      // Last Name
      lastNameVisible: [defaultConfig.lastNameVisible],
      lastNameRequired: [defaultConfig.lastNameRequired],

      // Email
      emailVisible: [defaultConfig.emailVisible],
      emailRequired: [defaultConfig.emailRequired],

      // Gender
      genderVisible: [defaultConfig.genderVisible],
      genderRequired: [defaultConfig.genderRequired],

      // Date of Birth - Main control
      dateOfBirthVisible: [defaultConfig.dateOfBirthVisible],
      dateOfBirthRequired: [defaultConfig.dateOfBirthRequired],
      dateOfBirthFormat: [defaultConfig.dateOfBirthFormat],

      // Date of Birth - Sub fields
      dayVisible: [defaultConfig.dayVisible],
      monthVisible: [defaultConfig.monthVisible],
      yearVisible: [defaultConfig.yearVisible],
      dayRequired: [{value: defaultConfig.dayRequired, disabled: true}],
      monthRequired: [{value: defaultConfig.monthRequired, disabled: true}],
      yearRequired: [{value: defaultConfig.yearRequired, disabled: true}]
    });

    // Subscribe to visibility changes to auto-disable required checkboxes
    this.profileConfigForm.get('firstNameVisible')?.valueChanges.subscribe(visible => {
      const ctrl = this.profileConfigForm.get('firstNameRequired');
      if (!visible) { ctrl?.setValue(false); ctrl?.disable({ emitEvent: false }); }
      else { ctrl?.enable({ emitEvent: false }); }
    });

    this.profileConfigForm.get('lastNameVisible')?.valueChanges.subscribe(visible => {
      const ctrl = this.profileConfigForm.get('lastNameRequired');
      if (!visible) { ctrl?.setValue(false); ctrl?.disable({ emitEvent: false }); }
      else { ctrl?.enable({ emitEvent: false }); }
    });

    this.profileConfigForm.get('emailVisible')?.valueChanges.subscribe(visible => {
      const ctrl = this.profileConfigForm.get('emailRequired');
      if (!visible) { ctrl?.setValue(false); ctrl?.disable({ emitEvent: false }); }
      else { ctrl?.enable({ emitEvent: false }); }
    });

    this.profileConfigForm.get('genderVisible')?.valueChanges.subscribe(visible => {
      const ctrl = this.profileConfigForm.get('genderRequired');
      if (!visible) { ctrl?.setValue(false); ctrl?.disable({ emitEvent: false }); }
      else { ctrl?.enable({ emitEvent: false }); }
    });

    this.profileConfigForm.get('dateOfBirthVisible')?.valueChanges.subscribe(visible => {
      const ctrl = this.profileConfigForm.get('dateOfBirthRequired');
      if (!visible) {
        ctrl?.setValue(false); ctrl?.disable({ emitEvent: false });
        // Auto-disable sub-fields when main Date of Birth is disabled
        this.profileConfigForm.patchValue({
          dayVisible: false,
          monthVisible: false,
          yearVisible: false
        });
      } else {
        ctrl?.enable({ emitEvent: false });
        // Re-apply format when Date of Birth is enabled
        const currentFormat = this.profileConfigForm.get('dateOfBirthFormat')?.value || 'FullDate';
        this.setDateSubFieldsFromFormat(currentFormat);
      }
    });

    // Auto-sync sub-field required checkboxes with main Date of Birth required
    this.profileConfigForm.get('dateOfBirthRequired')?.valueChanges.subscribe(required => {
      this.profileConfigForm.patchValue({
        dayRequired: required,
        monthRequired: required,
        yearRequired: required
      });
    });

    // Auto-sync sub-field required with their own visible status
    this.profileConfigForm.get('dayVisible')?.valueChanges.subscribe(visible => {
      this.profileConfigForm.get('dayRequired')?.setValue(visible);
    });

    this.profileConfigForm.get('monthVisible')?.valueChanges.subscribe(visible => {
      this.profileConfigForm.get('monthRequired')?.setValue(visible);
    });

    this.profileConfigForm.get('yearVisible')?.valueChanges.subscribe(visible => {
      this.profileConfigForm.get('yearRequired')?.setValue(visible);
    });

    // Initialize sub-field required values based on their visibility
    this.profileConfigForm.patchValue({
      dayRequired: this.profileConfigForm.get('dayVisible')?.value,
      monthRequired: this.profileConfigForm.get('monthVisible')?.value,
      yearRequired: this.profileConfigForm.get('yearVisible')?.value
    });

    // Subscribe to month visibility changes to enforce DateOfBirthFormat rules
    this.profileConfigForm.get('monthVisible')?.valueChanges.subscribe(monthVisible => {
      if (!monthVisible) {
        // If Month is disabled, we can only have YearOnly format
        // So disable Day as well (no Day+Year format exists)
        this.profileConfigForm.get('dayVisible')?.setValue(false);
      }
    });

    // Subscribe to year visibility changes
    this.profileConfigForm.get('yearVisible')?.valueChanges.subscribe(_ => {
      this.updateDateFormatFromSubFields();
    });

    // Subscribe to day visibility changes
    this.profileConfigForm.get('dayVisible')?.valueChanges.subscribe(_ => {
      this.updateDateFormatFromSubFields();
    });

    // Also subscribe to month and year changes to update format
    this.profileConfigForm.get('monthVisible')?.valueChanges.subscribe(() => {
      this.updateDateFormatFromSubFields();
    });

    this.profileConfigForm.get('yearVisible')?.valueChanges.subscribe(() => {
      this.updateDateFormatFromSubFields();
    });

    this.applyDisabledStates();
  }

  private applyDisabledStates() {
    const pairs: [string, string][] = [
      ['firstNameVisible', 'firstNameRequired'],
      ['lastNameVisible', 'lastNameRequired'],
      ['emailVisible', 'emailRequired'],
      ['genderVisible', 'genderRequired'],
      ['dateOfBirthVisible', 'dateOfBirthRequired'],
    ];
    for (const [visibleName, requiredName] of pairs) {
      const visible = this.profileConfigForm.get(visibleName)?.value;
      const ctrl = this.profileConfigForm.get(requiredName);
      visible ? ctrl?.enable({ emitEvent: false }) : ctrl?.disable({ emitEvent: false });
    }
  }

  private updateDateFormatFromSubFields() {
    const dayVisible = this.profileConfigForm.get('dayVisible')?.value;
    const monthVisible = this.profileConfigForm.get('monthVisible')?.value;
    const yearVisible = this.profileConfigForm.get('yearVisible')?.value;

    // If all sub-fields are disabled, disable Date of Birth parent
    if (!dayVisible && !monthVisible && !yearVisible) {
      this.profileConfigForm.get('dateOfBirthVisible')?.setValue(false, { emitEvent: false });
    }

    const newFormat = this.getDateFormatFromSubFields();
    // Update the hidden format value for saving
    this.profileConfigForm.get('dateOfBirthFormat')?.setValue(newFormat, { emitEvent: false });
  }

  private handleConfiguration() {
    if (!this.advertiser?.profileFieldConfiguration) {
      const defaultConfig = this.getDefaultConfiguration();
      this.profileConfigForm.patchValue(defaultConfig);
      return;
    }

    const config = this.advertiser.profileFieldConfiguration as any;

    this.profileConfigForm.patchValue({
      firstNameVisible: config.firstName?.isVisible ?? true,
      firstNameRequired: config.firstName?.isRequired ?? false,

      lastNameVisible: config.lastName?.isVisible ?? true,
      lastNameRequired: config.lastName?.isRequired ?? false,

      emailVisible: config.email?.isVisible ?? true,
      emailRequired: config.email?.isRequired ?? false,

      genderVisible: config.gender?.isVisible ?? true,
      genderRequired: config.gender?.isRequired ?? false,

      dateOfBirthVisible: config.dateOfBirth?.isVisible ?? true,
      dateOfBirthRequired: config.dateOfBirth?.isRequired ?? false,
      dateOfBirthFormat: config.dateOfBirth?.format ?? 'FullDate'
    });

    // Set date sub-fields based on format
    this.setDateSubFieldsFromFormat(config.dateOfBirth?.format ?? 'FullDate');

    this.applyDisabledStates();
  }

  private setDateSubFieldsFromFormat(format: string) {
    const dateOfBirthVisible = this.profileConfigForm.get('dateOfBirthVisible')?.value;

    // If Date of Birth is not visible, hide all sub-fields
    if (!dateOfBirthVisible) {
      this.profileConfigForm.patchValue({
        dayVisible: false,
        monthVisible: false,
        yearVisible: false
      });
      return;
    }

    // Set sub-fields based on format only if Date of Birth is visible
    switch (format) {
      case 'FullDate':
        this.profileConfigForm.patchValue({
          dayVisible: true,
          monthVisible: true,
          yearVisible: true
        });
        break;
      case 'DayAndMonth':
        this.profileConfigForm.patchValue({
          dayVisible: true,
          monthVisible: true,
          yearVisible: false
        });
        break;
      case 'MonthAndYear':
        this.profileConfigForm.patchValue({
          dayVisible: false,
          monthVisible: true,
          yearVisible: true
        });
        break;
      case 'YearOnly':
        this.profileConfigForm.patchValue({
          dayVisible: false,
          monthVisible: false,
          yearVisible: true
        });
        break;
      default:
        this.setDefaultDateFormat();
        break;
    }
  }

  private setDefaultDateFormat() {
    this.profileConfigForm.patchValue({
      dayVisible: true,
      monthVisible: true,
      yearVisible: true,
      dateOfBirthFormat: 'FullDate'
    });
  }

  private getDateFormatFromSubFields(): string {
    const dayVisible = this.profileConfigForm.get('dayVisible')?.value;
    const monthVisible = this.profileConfigForm.get('monthVisible')?.value;
    const yearVisible = this.profileConfigForm.get('yearVisible')?.value;

    if (dayVisible && monthVisible && yearVisible) {
      return 'FullDate';
    } else if (dayVisible && monthVisible) {
      return 'DayAndMonth';
    } else if (monthVisible && yearVisible) {
      return 'MonthAndYear';
    } else if (yearVisible) {
      return 'YearOnly';
    } else {
      // Default fallback - if nothing is visible, use FullDate
      return 'FullDate';
    }
  }

  getCurrentDateFormatLabel(): string {
    const format = this.getDateFormatFromSubFields();
    switch (format) {
      case 'FullDate':
        return 'Full Date (Day/Month/Year)';
      case 'DayAndMonth':
        return 'Day and Month';
      case 'MonthAndYear':
        return 'Month and Year';
      case 'YearOnly':
        return 'Year Only';
      default:
        return 'Full Date (Day/Month/Year)';
    }
  }

  async onSave() {
    if (this.profileConfigForm.invalid) {
      return;
    }

    this.isSaving = true;
    try {
      const formValue = this.profileConfigForm.getRawValue();

      // Get the correct date format based on sub-fields
      const dateFormat = this.getDateFormatFromSubFields();

      // Build the configuration object
      const profileFieldConfiguration = new ProfileFieldConfiguration({
        firstName: new ProfileFieldSettings({
          isVisible: formValue.firstNameVisible,
          isRequired: formValue.firstNameRequired
        }),
        lastName: new ProfileFieldSettings({
          isVisible: formValue.lastNameVisible,
          isRequired: formValue.lastNameRequired
        }),
        email: new ProfileFieldSettings({
          isVisible: formValue.emailVisible,
          isRequired: formValue.emailRequired
        }),
        gender: new ProfileFieldSettings({
          isVisible: formValue.genderVisible,
          isRequired: formValue.genderRequired
        }),
        dateOfBirth: new DateOfBirthFieldSettings({
          isVisible: formValue.dateOfBirthVisible,
          isRequired: formValue.dateOfBirthRequired,
          format: dateFormat as DateOfBirthFormat
        })
      });

      await lastValueFrom(this.client.updateProfileFieldConfiguration(this.advertiser.id, profileFieldConfiguration));

      this.snackBar.open('Profile field configuration saved successfully', 'Close', {
        duration: 3000
      });

      // Update the local advertiser object
      (this.advertiser as any).profileFieldConfiguration = profileFieldConfiguration;

    } catch (error) {
      console.error('Error saving profile field configuration:', error);
      this.snackBar.open('Error saving configuration. Please try again.', 'Close', {
        duration: 5000
      });
    } finally {
      this.isSaving = false;
    }
  }

  onReset() {
    // Reset form to default values without saving to DB
    const defaultConfiguration = this.getDefaultConfiguration();
    this.profileConfigForm.patchValue(defaultConfiguration);

    this.snackBar.open('Configuration reset to defaults', 'Close', {
      duration: 3000
    });
  }

}
